import { useCallback, useMemo, useState } from 'react';

import { LINKED_GROUPS_PAGE_SIZE } from '../../constants/jumper-sessions.js';
import {
  useActiveJumperSession,
  useCreateSuppression,
  useDetectionGroups,
  useDeviceSettings,
  useIntl,
  useStopJumperSession,
  useStopSuppression,
} from '../../hooks';
import { hzToMhz } from '../../lib/frequency.js';
import { formatNumber } from '../../lib/number-format.js';
import { type SignalType } from '../../types';
import {
  DetectionGroupRow,
  type DetectionGroupSuppressionSubmit,
} from '../detection-groups/DetectionGroupRow.js';
import { HIGH_PRIORITY_SUPPRESSION } from '../detection-groups/LinkedDeviceGroupsCompactTable.js';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui';
import { StopSessionConfirm } from './StopSessionConfirm.js';

type ActiveJumperSessionModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deviceId: string;
  canManageSuppressions: boolean;
};

function formatMmSs(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function ActiveJumperSessionModal({
  open,
  onOpenChange,
  deviceId,
  canManageSuppressions,
}: ActiveJumperSessionModalProps) {
  const intl = useIntl();
  const { session, secondsRemaining, isFetched } = useActiveJumperSession(deviceId);

  // Retain the parent group's identity (id + createdAt) so the linked-groups queryKey
  // survives the session ending (session → null). The query is disabled post-end;
  // feeding the retained identity keeps the queryKey stable so the last fetched list
  // stays cached instead of dropping to the empty state (spec AC7).
  const [lastParentGroup, setLastParentGroup] = useState<{
    id: string;
    createdAt: string;
  } | null>(null);
  // Session identity observed as of the last render, used to derive `lastParentGroup`
  // synchronously during render (see below) instead of one render late via an effect —
  // an effect-based clear would let a brand-new group-less PENDING session transiently
  // reuse the previous session's parent group + cached query key for one paint. Seeded
  // with a sentinel (not the current session) so the very first render's session, if any,
  // is also treated as "changed" and captured below rather than silently skipped.
  const [observedSession, setObservedSession] = useState<{
    id: string | undefined;
    detectionGroupId: string | null | undefined;
    detectionGroupCreatedAt: string | null | undefined;
  }>({
    id: undefined,
    detectionGroupId: undefined,
    detectionGroupCreatedAt: undefined,
  });
  if (
    session?.id !== observedSession.id ||
    session?.detectionGroupId !== observedSession.detectionGroupId ||
    session?.detectionGroupCreatedAt !== observedSession.detectionGroupCreatedAt
  ) {
    setObservedSession({
      id: session?.id,
      detectionGroupId: session?.detectionGroupId,
      detectionGroupCreatedAt: session?.detectionGroupCreatedAt,
    });
    if (session?.detectionGroupId && session.detectionGroupCreatedAt) {
      setLastParentGroup({
        id: session.detectionGroupId,
        createdAt: session.detectionGroupCreatedAt,
      });
    } else if (session?.id && !session.detectionGroupId) {
      // Active group-less session (new or still-pending) → drop any group retained
      // from a prior session; a group, once attached, never detaches (session === null
      // on end is the only other case, which falls through and keeps the retained group).
      setLastParentGroup(null);
    }
  }
  const parentGroupId = session?.detectionGroupId ?? lastParentGroup?.id ?? null;
  const parentGroupCreatedAt = session?.detectionGroupCreatedAt ?? lastParentGroup?.createdAt;

  const { data: deviceSettings } = useDeviceSettings(deviceId);
  const stopJumperSession = useStopJumperSession(deviceId);
  const createSuppression = useCreateSuppression();
  const stopSuppression = useStopSuppression();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [stopError, setStopError] = useState<string | null>(null);

  const { data: linkedGroupsData } = useDetectionGroups(
    {
      deviceId,
      parentDetectionGroupId: parentGroupId ?? undefined,
      parentDetectionGroupCreatedAt: parentGroupCreatedAt,
      pageSize: LINKED_GROUPS_PAGE_SIZE,
    },
    // Gate on a non-null group id too: a group-less (global) session is briefly PENDING
    // with detectionGroupId === null before the backend attaches its anchor — firing the
    // linked-groups query then would send an undefined parent and fetch nothing useful.
    { enabled: !!session && !!session.detectionGroupId },
  );

  const linkedGroups = useMemo(() => linkedGroupsData?.groups ?? [], [linkedGroupsData]);

  const deviceSuppressionDuration = deviceSettings?.suppressionTime ?? 0;
  const deviceMaxSuppressionTime = deviceSettings?.maxSuppressionTime ?? 0;

  const handleStopConfirm = useCallback(async () => {
    if (!session) return;
    setStopError(null);
    try {
      await stopJumperSession.mutateAsync(session.id);
      setConfirmOpen(false);
    } catch (e) {
      setConfirmOpen(false);
      setStopError(
        e instanceof Error ? e.message : intl.formatMessage({ id: 'common.unknownError' }),
      );
    }
  }, [session, stopJumperSession, intl]);

  const handleStartSuppression = useCallback(
    ({
      group,
      durationSec,
      isPriority,
      detectionGroupId,
      detectionGroupCreatedAt,
      bandwidthHz,
    }: DetectionGroupSuppressionSubmit) => {
      createSuppression.mutate({
        deviceId,
        payload: {
          channels: [
            {
              channelFrequency: group.frequency,
              type: group.signalType as SignalType,
              duration: durationSec,
              offset: 0,
              detectionGroupId,
              detectionGroupCreatedAt,
              ...(isPriority ? { priority: HIGH_PRIORITY_SUPPRESSION } : {}),
              ...(bandwidthHz !== undefined ? { bandwidth: bandwidthHz } : {}),
            },
          ],
        },
      });
    },
    [createSuppression, deviceId],
  );

  const handleStopSuppression = useCallback(
    (groupId: string, frequency: number, signalType: string, createdAt: string) => {
      stopSuppression.mutate({
        deviceId,
        payload: {
          channels: [
            {
              channelFrequency: frequency,
              type: signalType as SignalType,
              detectionGroupId: groupId,
              detectionGroupCreatedAt: createdAt,
            },
          ],
        },
      });
    },
    [stopSuppression, deviceId],
  );

  const handleChangeFrequency = useCallback(
    (_opts: { groupId: string; frequency: number; signalType: string; duration: number }) => {
      // no-op: frequency change not applicable in modal context
    },
    [],
  );

  const toggleGroupRow = useCallback((groupId: string) => {
    setExpandedGroup((prev) => (prev === groupId ? null : groupId));
  }, []);

  const isEnded = isFetched && session === null;
  const mmss = formatMmSs(secondsRemaining);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl p-8">
          <DialogHeader>
            <DialogTitle>
              {isEnded
                ? intl.formatMessage({ id: 'jumperSession.modal.sessionEnded' })
                : intl.formatMessage({ id: 'jumperSession.modal.title' })}
            </DialogTitle>
          </DialogHeader>

          {!isEnded && session && (
            <div className="min-w-0 space-y-3">
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm">
                  {formatNumber(hzToMhz(session.startFrequencyHz), { maximumFractionDigits: 5 })}
                  {' – '}
                  {formatNumber(hzToMhz(session.endFrequencyHz), { maximumFractionDigits: 5 })} MHz
                </span>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-2xl tabular-nums">{mmss}</span>
                  {canManageSuppressions && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setConfirmOpen(true)}
                      disabled={stopJumperSession.isPending}
                      data-testid="modal-stop-session-btn"
                    >
                      {intl.formatMessage({ id: 'jumperSession.modal.stop' })}
                    </Button>
                  )}
                </div>
              </div>

              {stopError && <div className="text-sm text-destructive">{stopError}</div>}
            </div>
          )}

          <div className="min-w-0 space-y-2">
            <h3 className="text-sm font-medium">
              {intl.formatMessage({ id: 'jumperSession.modal.linkedGroups' })}
            </h3>
            <div className="max-h-[60vh] overflow-auto">
              {linkedGroups.length === 0 ? (
                <p className="py-2 text-sm text-foreground">
                  {intl.formatMessage({ id: 'jumperSession.modal.empty' })}
                </p>
              ) : (
                <table className="w-full caption-bottom text-sm">
                  <tbody className="[&_tr:last-child]:border-0">
                    {linkedGroups.map((group) => (
                      <DetectionGroupRow
                        key={`${group.id}-${group.createdAt}`}
                        canManageSuppressions={canManageSuppressions}
                        changeFrequencyPending={false}
                        colSpan={11}
                        createSuppressionPending={createSuppression.isPending}
                        defaultSuppressionDuration={deviceSuppressionDuration}
                        deviceId={deviceId}
                        isExpanded={expandedGroup === group.id}
                        group={group}
                        maxSuppressionTime={deviceMaxSuppressionTime}
                        onChangeFrequency={handleChangeFrequency}
                        onStartSuppression={handleStartSuppression}
                        onStopSuppression={handleStopSuppression}
                        onToggleGroupRow={toggleGroupRow}
                        stopSuppressionPending={false}
                        showSuppressionActions={false}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <StopSessionConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={handleStopConfirm}
        isPending={stopJumperSession.isPending}
      />
    </>
  );
}
